import React, { useEffect, useState, useRef, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  TextField,
  Button,
  Box,
  Typography,
  Autocomplete,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Paper
} from '@mui/material';
import { Close, CloudUpload, AttachFile, Delete, Save } from '@mui/icons-material';
import { useQuery, useMutation } from '@apollo/client/react';
import { ProjectsListQuery, PROJECT_TREE_QUERY, DELETE_ATTACHMENT, GET_ELEMENT } from '../../graphql/projects';
import { TASK_QUERY, CREATE_OR_UPDATE_TASK, PEOPLE_QUERY, TYPE_OF_TASKS_QUERY, CREATE_OR_UPDATE_TYPE_OF_TASK } from '../../graphql/tasks';
import { UploadFiles } from '../../utils/uploads';
import LoadingOverlay from '../common/LoadingOverlay';

const TaskFormModal = ({ open, onClose, taskId, onSave }) => {
  const [formData, setFormData] = useState({
    title: '',
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    project: '',
    unit: '',
    companies: [],
    types: [],
    priority: 2,
    complete: 0,
    responsible: [],
    notes: ''
  });
  const [attachments, setAttachments] = useState([]);
  const [attachmentsToDelete, setAttachmentsToDelete] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [elements, setElements] = useState([]);
  const [elementCompanies, setElementCompanies] = useState([]);
  const fileInputRef = useRef(null);

  const priorityOptions = useMemo(() => [
    { value: 0, label: 'Highest' },
    { value: 1, label: 'High' },
    { value: 2, label: 'Normal' },
    { value: 3, label: 'Low' },
    { value: 4, label: 'Lowest' }
  ], []);

  const completeOptions = useMemo(() => 
    Array.from({length: 11}, (_, i) => ({ value: i * 10, label: `${i * 10}%` })), []);

  const priorityValue = useMemo(() => 
    priorityOptions.find(p => p.value === (formData.priority ?? 2)) || { value: 2, label: 'Normal' }, 
    [formData.priority, priorityOptions]);

  const completeValue = useMemo(() => 
    completeOptions.find(c => c.value === (formData.complete ?? 0)) || { value: 0, label: '0%' }, 
    [formData.complete, completeOptions]);

  // Queries
  const { data: taskData, loading: taskLoading } = useQuery(TASK_QUERY, {
    variables: { id: taskId },
    skip: !taskId || !open,
    fetchPolicy: 'network-only'
  });

  const { data: projectsData, loading: projectsLoading } = useQuery(ProjectsListQuery, {
    fetchPolicy: 'cache-and-network',
    skip: !open
  });

  const { data: treeData, loading: treeLoading } = useQuery(PROJECT_TREE_QUERY, {
    variables: { projectId: formData.project },
    skip: !formData.project || !open
  });

  const { data: elementData } = useQuery(GET_ELEMENT, {
    variables: { id: formData.unit },
    skip: !formData.unit || !open
  });

  const { data: projectData } = useQuery(GET_ELEMENT, {
    variables: { id: formData.project },
    skip: !formData.project || !open
  });

  // Calculate active company IDs (element or project fallback)
  const activeCompanyIds = useMemo(() => {
    const elementIds = elementData?.element?.company_ids || [];
    const projectIds = projectData?.element?.company_ids || [];
    return elementIds.length > 0 ? elementIds : projectIds;
  }, [elementData, projectData]);

  const { data: peopleData, loading: peopleLoading } = useQuery(PEOPLE_QUERY, {
    variables: {
      page: 1,
      size: 10000,
      filters: {
        active: "true",
        company_ids: activeCompanyIds
      }
    },
    skip: activeCompanyIds.length === 0 || !open,
    fetchPolicy: 'cache-first'
  });

  const { data: typeOfTasksData } = useQuery(TYPE_OF_TASKS_QUERY, {
    skip: !open
  });

  // Mutations
  const [createOrUpdateTask] = useMutation(CREATE_OR_UPDATE_TASK, {
    refetchQueries: [],
    awaitRefetchQueries: false
  });
  const [createOrUpdateTypeOfTask] = useMutation(CREATE_OR_UPDATE_TYPE_OF_TASK);
  const [deleteAttachment] = useMutation(DELETE_ATTACHMENT);

  // Load task data when editing
  useEffect(() => {
    if (taskData?.task) {
      const task = taskData.task;
      setFormData({
        title: task.name || '',
        startDate: task.start_date || '',
        endDate: task.end_date || '',
        project: task.project?.id || '',
        unit: task.element?.id || '',
        companies: task.companies?.map(c => c.id) || [],
        types: task.task_type?.map(t => parseInt(t.id)) || [],
        priority: task.priority ?? 2,
        complete: task.complete ?? 0,
        responsible: task.responsible?.map(r => parseInt(r.id)) || [],
        notes: task.description || ''
      });
      
      if (task.attachments) {
        setAttachments(task.attachments.map(att => ({
          ...att,
          isNew: false
        })));
      }
    } else if (!taskId) {
      // Reset form for new task
      setFormData({
        title: '',
        startDate: new Date().toISOString().split('T')[0],
        endDate: '',
        project: '',
        unit: '',
        company: '',
        companies: [],
        types: [],
        priority: 2,
        complete: 0,
        responsible: [],
        notes: ''
      });
      setAttachments([]);
      setAttachmentsToDelete([]);
    }
  }, [taskData, taskId]);

  // Reset dependencies when parent changes
  useEffect(() => {
    if (!taskId && !formData.project) {
      setFormData(prev => ({ ...prev, unit: '', company: '' }));
    }
  }, [formData.project, taskId]);

  useEffect(() => {
    if (!taskId && !formData.unit) {
      setFormData(prev => ({ ...prev, company: '' }));
    }
  }, [formData.unit, taskId]);

  useEffect(() => {
    if (!taskId && (!formData.companies || formData.companies.length === 0)) {
      setFormData(prev => ({ ...prev, responsible: [] }));
    }
  }, [formData.companies, taskId]);

  // Load element companies or fallback to project companies
  useEffect(() => {
    const companies = (projectsData?.companies?.list || []).filter(c => 
      activeCompanyIds.includes(c.id)
    );
    setElementCompanies(companies);
  }, [projectsData, activeCompanyIds]);

  // Flatten tree for elements
  useEffect(() => {
    if (treeData?.project_tree) {
      try {
        const treeData_parsed = JSON.parse(treeData.project_tree);
        const flatElements = [];
        
        const flattenTree = (items, depth = 0) => {
          items.forEach(item => {
            flatElements.push({
              ...item,
              depth,
              displayName: '\u00A0\u00A0\u00A0'.repeat(depth) + (item.label || item.name) + (item.number ? ` (${item.number})` : ''),
              cleanName: (item.label || item.name) + (item.number ? ` (${item.number})` : '')
            });
            if (item.children && item.children.length > 0) {
              flattenTree(item.children, depth + 1);
            }
          });
        };
        
        const itemsArray = Array.isArray(treeData_parsed) ? treeData_parsed : [treeData_parsed];
        flattenTree(itemsArray);
        setElements(flatElements);
      } catch (error) {
        setElements([]);
      }
    } else {
      setElements([]);
    }
  }, [treeData]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.project) newErrors.project = true;
    if (!formData.unit) newErrors.unit = true;
    if (!formData.title?.trim()) newErrors.title = true;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      // Delete marked attachments first
      for (const attachment of attachmentsToDelete) {
        await deleteAttachment({
          variables: {
            attachment_id: [parseInt(attachment.id)],
            attachable_id: parseInt(taskId),
            attachable_type: 'WlWorkflow::Task'
          }
        });
      }
      
      setAttachmentsToDelete([]);
      
      // Separate existing type IDs and new type names
      const existingTypeIds = [];
      const newTypeNames = [];
      
      formData.types.forEach(item => {
        if (typeof item === 'number') {
          existingTypeIds.push(item);
        } else if (typeof item === 'string') {
          newTypeNames.push(item);
        }
      });
      
      // Create new types and get their IDs
      const newTypeIds = [];
      for (const name of newTypeNames) {
        const { data } = await createOrUpdateTypeOfTask({ variables: { name } });
        if (data?.createOrUpdateTypeOfTask?.type_of_task?.id) {
          newTypeIds.push(parseInt(data.createOrUpdateTypeOfTask.type_of_task.id));
        }
      }
      
      const variables = {
        name: formData.title,
        project_id: parseInt(formData.project),
        element_id: parseInt(formData.unit),
        company_ids: formData.companies && formData.companies.length > 0 ? formData.companies.map(id => parseInt(id)) : [],
        start_date: formData.startDate || null,
        end_date: formData.endDate || null,
        dictionary_task_type_ids: [...existingTypeIds, ...newTypeIds],
        priority: formData.priority ?? 2,
        complete: formData.complete ?? 0,
        responsible_ids: formData.responsible,
        description: formData.notes || null
      };

      if (taskId) {
        variables.id = taskId;
      }

      const { data } = await createOrUpdateTask({ variables });
      
      if (data?.createOrUpdateTask?.errors?.length > 0) {
        setLoading(false);
        return;
      }
      
      const savedTask = data?.createOrUpdateTask?.task;
      
      // Upload new attachments
      const newFiles = attachments.filter(att => att.isNew && att.file);
      if (newFiles.length > 0 && savedTask?.id) {
        const files = newFiles.map(att => att.file);
        await UploadFiles(savedTask.id, 'WlWorkflow::Task', files);
      }
      
      if (onSave) {
        await onSave(savedTask);
      }
      onClose();
    } catch (error) {
      console.error('Error handling task:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (files) => {
    const validFormats = ['png', 'jpg', 'jpeg', 'pdf', 'dwg', 'svg'];
    const newFiles = Array.from(files).filter(file => {
      const extension = file.name.split('.').pop().toLowerCase();
      return validFormats.includes(extension);
    });

    const fileObjects = newFiles.map(file => ({
      id: Date.now() + Math.random(),
      data_file_name: file.name,
      file: file,
      isNew: true
    }));

    setAttachments(prev => [...prev, ...fileObjects]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const removeAttachment = (attachment) => {
    if (attachment.isNew) {
      setAttachments(prev => prev.filter(att => att.id !== attachment.id));
    } else {
      setAttachmentsToDelete(prev => [...prev, attachment]);
      setAttachments(prev => prev.filter(att => att.id !== attachment.id));
    }
  };

  if (!open) {
    return null;
  }

  if (taskLoading || projectsLoading) {
    return <LoadingOverlay />;
  }

  const projects = projectsData?.projects?.list || [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {taskId ? taskData?.task?.name || 'Edit Task' : 'Create New Task'}
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Paper sx={{ p: 3, width: '100%', position: 'relative' }}>
          {loading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 9999,
                borderRadius: 1
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                  animation: 'pulse 1.5s ease-in-out infinite'
                }}
              >
                <Save sx={{ fontSize: 48, color: 'primary.main' }} />
                <Typography variant="h6" color="primary.main">
                  {taskId ? 'Updating task...' : 'Creating task...'}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Projects and Units */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Autocomplete
              options={projects}
              getOptionLabel={(option) => `${option.name}${option.number ? ` (${option.number})` : ''}`}
              value={projects.find(p => p.id === formData.project) || null}
              onChange={(event, newValue) => setFormData({ ...formData, project: newValue?.id || '' })}
              renderInput={(params) => <TextField {...params} label="Project *" size="small" error={errors.project} helperText={errors.project ? "Required" : ""} />}
              sx={{ flex: 1 }}
            />
            <Autocomplete
              options={elements}
              getOptionLabel={(option) => option.cleanName}
              renderOption={(props, option) => {
                const { key, ...otherProps } = props;
                return (
                  <li key={key} {...otherProps}>
                    {option.displayName}
                  </li>
                );
              }}
              value={elements.find(e => e.id === formData.unit) || null}
              onChange={(event, newValue) => setFormData({ ...formData, unit: newValue?.id || '' })}
              disabled={!formData.project || treeLoading}
              renderInput={(params) => <TextField {...params} label={!formData.project ? "Select project first" : treeLoading ? "Loading..." : "Element *"} size="small" error={errors.unit} helperText={errors.unit ? "Required" : ""} />}
              sx={{ flex: 1 }}
            />
          </Box>
          
          <TextField
            fullWidth
            label="Title *"
            value={formData.title}
            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
            size="small"
            error={errors.title}
            helperText={errors.title ? "Required" : ""}
            sx={{ mb: 2 }}
          />
          
          {/* Dates, Priority, Complete */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              label="Start Date"
              type="date"
              value={formData.startDate}
              onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{ flex: 1 }}
            />
            <TextField
              label="End Date"
              type="date"
              value={formData.endDate}
              onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{ flex: 1 }}
            />
            <Autocomplete
              options={priorityOptions}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              value={priorityValue}
              onChange={(event, newValue) => {
                const priorityVal = newValue ? newValue.value : 2;
                setFormData(prev => ({ ...prev, priority: priorityVal }));
              }}
              renderInput={(params) => <TextField {...params} label="Priority" size="small" />}
              sx={{ flex: 1 }}
            />
            <Autocomplete
              options={completeOptions}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              value={completeValue}
              onChange={(event, newValue) => setFormData({ ...formData, complete: newValue?.value ?? 0 })}
              renderInput={(params) => <TextField {...params} label="% complete" size="small" />}
              sx={{ flex: 1 }}
            />
          </Box>

          {/* Types, Company, Responsible */}
          <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
            <Autocomplete
              multiple
              freeSolo
              options={typeOfTasksData?.type_of_tasks || []}
              getOptionLabel={(option) => typeof option === 'string' ? option : option.name}
              isOptionEqualToValue={(option, value) => {
                if (typeof value === 'string') return false;
                return parseInt(option.id) === parseInt(value.id || value);
              }}
              value={formData.types.map(item => {
                if (typeof item === 'string') return item;
                const found = (typeOfTasksData?.type_of_tasks || []).find(t => parseInt(t.id) === item);
                return found || item;
              })}
              onChange={(event, newValue) => {
                const types = newValue.map(item => {
                  if (typeof item === 'string') return item;
                  return parseInt(item.id);
                });
                setFormData({ ...formData, types });
              }}
              renderInput={(params) => <TextField {...params} label="Types" size="small" placeholder="Type or select..." />}
              sx={{ flex: 1 }}
            />
            <Autocomplete
              multiple
              options={elementCompanies}
              getOptionLabel={(option) => option.name}
              value={elementCompanies.filter(c => formData.companies && formData.companies.includes(c.id))}
              onChange={(event, newValue) => setFormData({ ...formData, companies: newValue.map(c => c.id) })}
              disabled={!elementCompanies.length}
              renderInput={(params) => <TextField {...params} label={elementCompanies.length === 0 ? "Loading companies..." : "Companies"} size="small" />}
              sx={{ flex: 1 }}
            />
            <Autocomplete
              key={`responsible-${formData.companies.join(',')}`}
              multiple
              options={[...(peopleData?.people?.list || [])].sort((a, b) => `${a.last_name} ${a.first_name}`.localeCompare(`${b.last_name} ${b.first_name}`))}
              getOptionLabel={(option) => `${option.last_name} ${option.first_name} (${option.uuid})`}
              isOptionEqualToValue={(option, value) => parseInt(option.id) === parseInt(value.id || value)}
              value={(peopleData?.people?.list || []).filter(p => formData.responsible.includes(parseInt(p.id)))}
              onChange={(event, newValue) => setFormData({ ...formData, responsible: newValue.map(p => parseInt(p.id)) })}
              disabled={!formData.companies || formData.companies.length === 0 || peopleLoading}
              renderInput={(params) => <TextField {...params} label={!formData.companies || formData.companies.length === 0 ? "Select companies first" : peopleLoading ? "Loading..." : "Responsible"} size="small" />}
              sx={{ flex: 1 }}
            />
          </Box>

          {/* Description */}
          <TextField
            fullWidth
            label="Description"
            multiline
            rows={4}
            value={formData.notes}
            onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
            size="small"
            sx={{ mb: 1.5 }}
          />

          {/* Attachments */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5, fontSize: '0.85rem' }}>Attachments</Typography>
            <Box
              sx={{
                border: '1px dashed',
                borderColor: dragOver ? 'primary.main' : 'grey.300',
                borderRadius: 1,
                p: 1.5,
                textAlign: 'center',
                cursor: 'pointer',
                backgroundColor: dragOver ? 'action.hover' : 'transparent',
                transition: 'all 0.2s ease'
              }}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => fileInputRef.current?.click()}
            >
              <CloudUpload sx={{ fontSize: 32, color: 'grey.400', mb: 0.5 }} />
              <Typography variant="body2" sx={{ mb: 0.5, fontSize: '0.8rem' }}>Drop files or click</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                PNG, JPG, PDF, DWG, SVG
              </Typography>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".png,.jpg,.jpeg,.pdf,.dwg,.svg"
                style={{ display: 'none' }}
                onChange={(e) => handleFileSelect(e.target.files)}
              />
            </Box>

            {attachments.length > 0 && (
              <List sx={{ mt: 1 }}>
                {attachments.map((attachment) => (
                  <ListItem key={attachment.id} sx={{ border: '1px solid', borderColor: 'grey.200', borderRadius: 1, mb: 0.5, py: 0.5 }}>
                    <AttachFile sx={{ mr: 0.5, color: 'grey.600', fontSize: '1rem' }} />
                    <ListItemText primary={attachment.data_file_name} primaryTypographyProps={{ fontSize: '0.8rem' }} />
                    <ListItemSecondaryAction>
                      <IconButton onClick={() => removeAttachment(attachment)} size="small">
                        <Delete />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>

          {/* Actions */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} variant="contained" disabled={loading}>
              {taskId ? 'Update' : 'Create'}
            </Button>
          </Box>
        </Paper>
      </DialogContent>
    </Dialog>
  );
};

export default TaskFormModal;
